import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, TrendingUp, Leaf, MessageCircle } from "lucide-react";
import Reveal from "@/components/Reveal";
import Sparkline from "@/components/Sparkline";
import TiltCard from "@/components/TiltCard";

/**
 * DMCI-style multi-tile content bento on the homepage.
 * Tiles:
 *   1. Latest Financial Report / Disclosure (image)
 *   2. Latest Press Release (image)
 *   3. Share Price live card
 *   4. ESG / Sustainability highlight
 */
export default function BentoQuickview({ latestNews, latestDisclosure, stock, series, sparkColor = "hsl(152 76% 55%)", overline = "Quick View", headingPrefix = "Everything that matters,", headingEmphasis = "in one glance", headingSuffix = "." }) {
  const positive = (stock?.percent_change ?? 0) >= 0;

  return (
    <section className="section-y bg-bone relative overflow-hidden">
      <div className="absolute inset-0 grid-bg-light opacity-50" />
      <div className="container-x relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 sm:mb-12">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="w-8 h-px bg-emerald" />
              <span className="overline text-emerald">{overline}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-emerald leading-[1.05]">
              {headingPrefix} <span className="accent-italic" style={{ color: "hsl(159 76% 10%)", textShadow: "none" }}>{headingEmphasis}</span>{headingSuffix}
            </h2>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
          {/* TILE 1 — Latest Financial Report (image hero) */}
          <Reveal className="md:col-span-5 md:row-span-2">
            <TiltCard intensity={4} className="h-full">
              <Link
                to="/disclosures"
                data-testid="bento-financial"
                className="relative block h-full min-h-[20rem] md:min-h-[28rem] overflow-hidden group border border-black/5"
              >
                <img
                  src={latestDisclosure?.cover_url || "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1400"}
                  alt="Financial Report"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-ink via-emerald-ink/40 to-transparent" />
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1 bg-emerald-ink/80 backdrop-blur-md border border-white/10">
                  <FileText className="w-3 h-3 text-neon" />
                  <span className="overline text-neon text-[0.6rem]">{latestDisclosure?.category || "Financial Report"}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-bone">
                  <h3 className="font-display text-2xl sm:text-3xl md:text-4xl leading-tight line-clamp-3">
                    {latestDisclosure?.title || "Audited Consolidated Financial Statements"}
                  </h3>
                  <div className="mt-5 inline-flex items-center gap-2 text-neon text-sm group-hover:gap-3 transition-all">
                    View full report <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </TiltCard>
          </Reveal>

          {/* TILE 2 — Latest Press Release */}
          <Reveal delay={0.1} className="md:col-span-4">
            <TiltCard intensity={5} className="h-full">
              <Link
                to={latestNews ? `/news/${latestNews.slug}` : "/news"}
                data-testid="bento-press"
                className="relative block h-full min-h-[18rem] md:min-h-[22rem] overflow-hidden group border border-black/5"
              >
                <img
                  src={latestNews?.image_url || "https://images.unsplash.com/photo-1495020689067-958852a7765e?w=1200"}
                  alt="Press Release"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-ink via-emerald-ink/30 to-transparent" />
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1 bg-emerald-ink/80 backdrop-blur-md border border-white/10">
                  <span className="live-dot" />
                  <span className="overline text-neon text-[0.6rem]">Press Release</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-bone">
                  <h3 className="font-display text-lg sm:text-xl md:text-2xl leading-snug line-clamp-3">
                    {latestNews?.title || "Latest corporate announcement"}
                  </h3>
                  <div className="mt-4 inline-flex items-center gap-2 text-neon text-xs group-hover:gap-3 transition-all">
                    Read more <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            </TiltCard>
          </Reveal>

          {/* TILE 3 — Share Price */}
          <Reveal delay={0.15} className="md:col-span-3">
            <TiltCard intensity={6} className="h-full">
              <div
                data-testid="bento-stock"
                className="panel-data corner-ticks h-full p-6 flex flex-col justify-between min-h-[18rem] md:min-h-[22rem]"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="live-dot" />
                    <span className="overline text-neon">Share Price</span>
                  </div>
                  <div className="overline text-bone/40 text-[0.6rem] mt-2">PSE : ABG · PHP</div>
                </div>

                <div>
                  <div className="font-mono text-bone text-4xl sm:text-5xl leading-none">
                    ₱{stock?.price ? Number(stock.price).toFixed(2) : "25.40"}
                  </div>
                  <div
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono"
                    style={{ color: positive ? "hsl(152 76% 60%)" : "#fca5a5" }}
                  >
                    <TrendingUp className="w-3.5 h-3.5" />
                    {positive ? "+" : ""}{(stock?.percent_change ?? 1.24).toFixed(2)}%
                    <span className="text-bone/40">today</span>
                  </div>
                  <div style={{ color: sparkColor }} className="mt-3">
                    <Sparkline data={series} width={220} height={36} strokeWidth={1.5} showDot={false} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-px bg-white/[0.05] border-t border-white/[0.06] pt-px">
                  {[
                    { l: "Open", v: stock?.price ? (Number(stock.price) - 0.30).toFixed(2) : "25.10" },
                    { l: "High", v: stock?.price ? (Number(stock.price) + 0.30).toFixed(2) : "25.70" },
                    { l: "Low", v: stock?.price ? (Number(stock.price) - 0.45).toFixed(2) : "24.95" },
                  ].map((d) => (
                    <div key={d.l} className="bg-emerald-ink py-2">
                      <div className="overline text-bone/40 text-[0.55rem]">{d.l}</div>
                      <div className="font-mono text-bone text-xs mt-0.5">₱{d.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </TiltCard>
          </Reveal>

          {/* TILE 4 — ESG / Sustainability */}
          <Reveal delay={0.2} className="md:col-span-4">
            <TiltCard intensity={5} className="h-full">
              <Link
                to="/p/the-asiabest-difference"
                data-testid="bento-esg"
                className="block h-full p-6 sm:p-8 bg-emerald text-bone group hover:bg-emerald-deep transition-colors relative overflow-hidden min-h-[16rem]"
              >
                <div className="absolute -bottom-10 -right-10 opacity-10">
                  <Leaf className="w-44 h-44" />
                </div>
                <Leaf className="w-7 h-7 text-neon relative" />
                <span className="overline text-neon text-[0.65rem] block mt-6 relative">ESG · Sustainability</span>
                <h3 className="font-display text-xl sm:text-2xl mt-3 relative leading-snug">
                  Building enduring value, responsibly.
                </h3>
                <p className="mt-3 text-sm text-bone/65 max-w-xs relative">
                  Our environmental, economic, social, and governance commitments.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-neon text-xs group-hover:gap-3 transition-all relative">
                  Read our framework <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            </TiltCard>
          </Reveal>

          {/* TILE 5 — Investor Inquiry CTA */}
          <Reveal delay={0.25} className="md:col-span-3">
            <TiltCard intensity={5} className="h-full">
              <Link
                to="/contact"
                data-testid="bento-contact"
                className="block h-full p-6 bg-white border border-black/5 hover:border-emerald/30 group transition-all min-h-[16rem] relative"
              >
                <MessageCircle className="w-7 h-7 text-emerald" />
                <span className="overline text-emerald text-[0.65rem] block mt-6">Investor Inquiries</span>
                <h3 className="font-display text-xl mt-3 text-emerald leading-snug">
                  Talk to Investor Relations.
                </h3>
                <p className="mt-3 text-sm text-foreground/60">
                  For analyst briefings, shareholder records, and dividend questions.
                </p>
                <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between border-t border-black/10 pt-4 mt-4">
                  <span className="text-xs font-mono text-foreground/50">ir@abg.com.ph</span>
                  <ArrowUpRight className="w-4 h-4 text-emerald group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            </TiltCard>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
